import { TIME_FORMAT } from '../constants';

/**
 * Format time in milliseconds to MM:SS.mmm format with three decimal places
 * Includes input validation and normalization for times >= 1 hour.
 */
export const formatTime = (timeInMs: number): string => {
  // --- Start: Added safety checks from safeFormatTime ---
  if (typeof timeInMs !== 'number' || !isFinite(timeInMs) || timeInMs < 0) {
    return '00:00.000'; // Return default format for invalid input
  }
  // --- End: Added safety checks ---

  let minutes: number;
  let seconds: number;
  let milliseconds: number;

  // --- Start: Added normalization logic from safeFormatTime ---
  // Cap at reasonable values - handle potential hours display robustly
  if (timeInMs >= 3600000) {
    // 1 hour in ms
    // Normalize to minutes and seconds within the hour for MM:SS.mmm display
    const totalSeconds = Math.floor(timeInMs / 1000);
    minutes = Math.floor((totalSeconds % 3600) / 60); // Minutes part of the hour
    seconds = totalSeconds % 60; // Seconds part of the minute
    milliseconds = Math.floor(timeInMs % 1000); // Milliseconds part
    // console.warn(`formatTime: Input time ${timeInMs}ms >= 1 hour. Displaying normalized MM:SS.mmm.`); // Keep commented out unless debugging
  } else {
    // Original logic for times < 1 hour
    minutes = Math.floor(timeInMs / TIME_FORMAT.MINUTES_IN_MS);
    seconds = Math.floor((timeInMs % TIME_FORMAT.MINUTES_IN_MS) / TIME_FORMAT.SECONDS_IN_MS);
    milliseconds = Math.floor(timeInMs % TIME_FORMAT.SECONDS_IN_MS);
  }
  // --- End: Added normalization logic ---

  // Format parts
  const formattedMinutes = minutes.toString().padStart(TIME_FORMAT.PAD_LENGTH, '0');
  const formattedSeconds = seconds.toString().padStart(TIME_FORMAT.PAD_LENGTH, '0');
  const formattedMilliseconds = milliseconds.toString().padStart(TIME_FORMAT.MS_PAD_LENGTH, '0');

  // Return formatted string
  return `${formattedMinutes}:${formattedSeconds}.${formattedMilliseconds}`;
};

/**
 * Format time in milliseconds to MM:SS.m format with one decimal place
 */
export const formatTimeShort = (timeInMs: number): string => {
  if (typeof timeInMs !== 'number') return '00:00.0';

  const minutes = Math.floor(timeInMs / TIME_FORMAT.MINUTES_IN_MS);
  const seconds = Math.floor((timeInMs % TIME_FORMAT.MINUTES_IN_MS) / TIME_FORMAT.SECONDS_IN_MS);
  const milliseconds = Math.floor(timeInMs % TIME_FORMAT.SECONDS_IN_MS);

  // Format parts
  const formattedMinutes = minutes.toString().padStart(TIME_FORMAT.PAD_LENGTH, '0');
  const formattedSeconds = seconds.toString().padStart(TIME_FORMAT.PAD_LENGTH, '0');
  // Only keep the first digit of milliseconds (one decimal place)
  const formattedMilliseconds = Math.floor(milliseconds / 100).toString();

  // Return formatted string
  return `${formattedMinutes}:${formattedSeconds}.${formattedMilliseconds}`;
};

/**
 * Format pace in minutes per unit (mile or kilometer)
 */
export const formatPace = (
  pace: number | null | undefined,
  unit: 'mile' | 'km' = 'mile'
): string => {
  // Comprehensive check for invalid values
  if (pace === null || pace === undefined || !isFinite(pace) || pace <= 0) {
    return `--:-- ${unit === 'mile' ? '/mile' : '/km'}`;
  }

  try {
    const minutes = Math.floor(pace);
    const seconds = Math.floor((pace - minutes) * 60);
    const unitLabel = unit === 'mile' ? '/mile' : '/km';

    // Additional validation to avoid NaN or Infinity
    if (!isFinite(minutes) || !isFinite(seconds)) {
      return `--:-- ${unitLabel}`;
    }

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} ${unitLabel}`;
  } catch (e) {
    // Fallback in case of any error
    return `--:-- ${unit === 'mile' ? '/mile' : '/km'}`;
  }
};

/**
 * Format a date for display
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString();
};
